import React from 'react'

interface ErrorInfo {
  name?: string
  message?: string
  code?: string
  statusCode?: number
  userMessage?: string
  action?: string
}

interface ErrorDisplayProps {
  error: Error | ErrorInfo | string
  onRetry?: () => void
  onContactSupport?: () => void
  className?: string
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ 
  error, 
  onRetry, 
  onContactSupport,
  className = ''
}) => {
  // Parse error information
  const errorInfo = React.useMemo(() => {
    if (typeof error === 'string') {
      return { userMessage: error, code: 'UNKNOWN' }
    }
    
    if (error instanceof Error) {
      // Try to parse Convex error format
      try {
        const parsed = JSON.parse(error.message)
        if (parsed.userMessage) {
          return parsed as ErrorInfo
        }
      } catch {
        // Not a JSON error, use the error message
      }
      
      // Handle authorization errors by message content
      if (error.message.includes('Unauthorized')) {
        if (error.message.includes('Authentication required')) {
          return {
            code: 'AUTHENTICATION_REQUIRED',
            userMessage: 'Please sign in to access this feature.',
            action: 'SIGN_IN'
          }
        }
        if (error.message.includes('Admin') || error.message.includes('Manager')) {
          return {
            code: 'ROLE_REQUIRED',
            userMessage: 'This feature requires elevated permissions.',
            action: 'CONTACT_ADMIN'
          }
        }
        return {
          code: 'INSUFFICIENT_PERMISSIONS',
          userMessage: 'You don\'t have permission to access this resource.',
          action: 'CONTACT_ADMIN'
        }
      }
      
      return {
        userMessage: 'Something went wrong. Please try again.',
        code: 'UNKNOWN'
      }
    }
    
    return error as ErrorInfo
  }, [error])

  const getIcon = () => {
    switch (errorInfo.code) {
      case 'AUTHENTICATION_REQUIRED':
        return '🔐'
      case 'INSUFFICIENT_PERMISSIONS':
      case 'ROLE_REQUIRED':
        return '🚫'
      case 'INACTIVE_USER':
        return '⚠️'
      default:
        return '❌'
    }
  }

  const getTitle = () => {
    switch (errorInfo.code) {
      case 'AUTHENTICATION_REQUIRED':
        return 'Sign In Required'
      case 'INSUFFICIENT_PERMISSIONS':
        return 'Access Denied'
      case 'ROLE_REQUIRED':
        return 'Elevated Permissions Required'
      case 'INACTIVE_USER':
        return 'Account Inactive'
      default:
        return 'Error'
    }
  }

  const handleAction = () => {
    switch (errorInfo.action) {
      case 'SIGN_IN':
        window.location.href = '/login'
        break
      case 'CONTACT_ADMIN':
        if (onContactSupport) {
          onContactSupport()
        } else {
          // Default contact action
          alert('Please contact your system administrator for assistance.')
        }
        break
      default:
        if (onRetry) {
          onRetry()
        }
    }
  }

  const getActionLabel = () => {
    switch (errorInfo.action) {
      case 'SIGN_IN':
        return 'Sign In'
      case 'CONTACT_ADMIN':
        return 'Contact Administrator'
      default:
        return onRetry ? 'Try Again' : undefined
    }
  }

  return (
    <div className={`error-display ${className}`} style={{
      padding: '24px',
      borderRadius: '12px',
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '20px auto'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>
        {getIcon()}
      </div>
      
      <h3 style={{
        color: '#dc2626',
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '12px',
        margin: '0 0 12px 0'
      }}>
        {getTitle()}
      </h3>
      
      <p style={{
        color: '#7f1d1d',
        fontSize: '16px',
        lineHeight: '1.5',
        marginBottom: '20px',
        margin: '0 0 20px 0'
      }}>
        {errorInfo.userMessage}
      </p>
      
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        {getActionLabel() && (
          <button
            onClick={handleAction}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#b91c1c'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626'
            }}
          >
            {getActionLabel()}
          </button>
        )}
        
        {onRetry && errorInfo.action !== 'SIGN_IN' && (
          <button
            onClick={onRetry}
            style={{
              backgroundColor: 'transparent',
              color: '#dc2626',
              border: '1px solid #dc2626',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626'
              e.currentTarget.style.color = 'white'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = '#dc2626'
            }}
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  )
}

export default ErrorDisplay